import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Layout,
  Table,
  Breadcrumb,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Input,
  Button,
  Statistic,
  Tag,
  Select,
  Progress,
  Badge,
  Modal,
  message,
  Checkbox,
  DatePicker,
} from "antd";
import { gql, useQuery, useMutation } from "@apollo/client";
import moment from "moment-timezone";
import { SearchOutlined, ReloadOutlined, TrophyOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

moment.tz.setDefault("Asia/Bangkok");
const { Title } = Typography;
const { Option } = Select;

const GET_EVENT = gql`
  query getEvent($eventId: MongoID!) {
    eventById(_id: $eventId) {
      _id
      name
      slug
      organizId
      startTime
      endTime
    }
  }
`;

const GET_RESULTS_MONITORING = gql`
  query resultsMonitoring($slug: String!, $race: String, $leaderboardLimit: Int, $recentLimit: Int) {
    resultsMonitoring(slug: $slug, race: $race, leaderboardLimit: $leaderboardLimit, recentLimit: $recentLimit) {
      summary {
        totalRunners
        runnersStarted
        runnersFinished
        runnersInProgress
        recentFinishers
      }
      recentResults {
        bib
        name
        position
        time
        race
      }
      leaderboard {
        bib
        name
        race
        team
        gender
        currentPosition
        lastCheckpointTime
        totalCheckpoints
        requiredCheckpoints
        progress
        finalTime
        status
        missingCheckpoints
      }
      lastUpdate
    }
  }
`;

const ADD_MISSING_CHECKPOINTS = gql`
  mutation addMissingCheckpoints($slug: String!, $bib: Int, $bibs: [Int], $race: String, $positions: [Int], $defaultTime: String) {
    addMissingCheckpoints(slug: $slug, bib: $bib, bibs: $bibs, race: $race, positions: $positions, defaultTime: $defaultTime) {
      bib
      race
      missingPositions
      addedCount
      success
      message
    }
  }
`;

function ResultsMonitoring() {
  const { eventId, organizId } = useParams();
  const [searchText, setSearchText] = useState("");
  const [selectedRace, setSelectedRace] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // First get event to retrieve slug
  const { data: eventData, loading: eventLoading } = useQuery(GET_EVENT, {
    variables: { eventId },
  });

  const slug = eventData?.eventById?.slug;
  const event = eventData?.eventById;

  const [addMissingCheckpoints, { loading: addingCheckpoints }] = useMutation(ADD_MISSING_CHECKPOINTS);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [positionModalVisible, setPositionModalVisible] = useState(false);
  const [selectedBib, setSelectedBib] = useState(null);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [checkpointDateTime, setCheckpointDateTime] = useState(null);

  // Then query resultsMonitoring with slug - fetch all data, filter by race on client
  const { data, loading, refetch } = useQuery(GET_RESULTS_MONITORING, {
    variables: { 
      slug: slug || "",
      race: null, // Always fetch all races, filter on client
      leaderboardLimit: null, // No limit - show all data
      recentLimit: null, // No limit - show all data
    },
    skip: !slug,
    pollInterval: autoRefresh ? 10000 : 0,
  });

  // Debug: Log modal state changes
  useEffect(() => {
    console.log("Modal state changed - visible:", positionModalVisible, "bib:", selectedBib, "availablePositions:", availablePositions);
  }, [positionModalVisible, selectedBib, availablePositions]);

  if (eventLoading || loading || !data) return <div>loading...</div>;

  const { resultsMonitoring } = data;
  const { summary: originalSummary, recentResults, leaderboard, lastUpdate } = resultsMonitoring || {};

  // Filter leaderboard based on race first (client-side filtering)
  let filteredLeaderboard = leaderboard || [];
  
  // Filter by race first (client-side)
  if (selectedRace) {
    filteredLeaderboard = filteredLeaderboard.filter(
      (item) => item.race === selectedRace
    );
  }
  
  // Then filter by search text
  if (searchText) {
    filteredLeaderboard = filteredLeaderboard.filter(
      (item) =>
        item.bib.toString().includes(searchText) ||
        (item.name || "").toLowerCase().includes(searchText.toLowerCase())
    );
  }

  // Sort by finish time (finalTime)
  // Priority: finished (by finalTime) > in_progress (by lastCheckpointTime) > not_started
  // Create a copy of the array before sorting (since the original is read-only)
  filteredLeaderboard = [...filteredLeaderboard].sort((a, b) => {
    // Status priority: finished > in_progress > not_started
    const statusPriority = { finished: 1, in_progress: 2, not_started: 3 };
    const aPriority = statusPriority[a.status] || 99;
    const bPriority = statusPriority[b.status] || 99;
    
    // If different status, sort by status priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // If both finished, sort by finalTime (ascending - fastest first)
    if (a.status === "finished" && b.status === "finished") {
      if (!a.finalTime) return 1;
      if (!b.finalTime) return -1;
      // Compare time strings in HH:mm:ss format
      return a.finalTime.localeCompare(b.finalTime);
    }
    
    // If both in_progress, sort by lastCheckpointTime (most recent first)
    if (a.status === "in_progress" && b.status === "in_progress") {
      if (!a.lastCheckpointTime) return 1;
      if (!b.lastCheckpointTime) return -1;
      return moment(b.lastCheckpointTime).unix() - moment(a.lastCheckpointTime).unix();
    }
    
    // For not_started or same status without time, maintain original order
    return 0;
  });

  // Filter recent results based on race first (client-side filtering)
  let filteredRecentResults = recentResults || [];
  
  // Filter by race first (client-side)
  if (selectedRace) {
    filteredRecentResults = filteredRecentResults.filter(
      (item) => item.race === selectedRace
    );
  }
  
  // Then filter by search text
  if (searchText) {
    filteredRecentResults = filteredRecentResults.filter(
      (item) =>
        item.bib.toString().includes(searchText) ||
        (item.name || "").toLowerCase().includes(searchText.toLowerCase())
    );
  }

  // Calculate summary statistics from filtered leaderboard (client-side)
  const filteredSummary = selectedRace ? {
    totalRunners: filteredLeaderboard.length,
    runnersStarted: filteredLeaderboard.filter(r => r.status !== "not_started").length,
    runnersFinished: filteredLeaderboard.filter(r => r.status === "finished").length,
    runnersInProgress: filteredLeaderboard.filter(r => r.status === "in_progress").length,
    recentFinishers: filteredLeaderboard.filter(r => 
      r.status === "finished" && 
      r.lastCheckpointTime && 
      moment(r.lastCheckpointTime).isAfter(moment().subtract(1, "hour"))
    ).length,
  } : originalSummary;

  const summary = filteredSummary || originalSummary;

  // Get unique races for filter (from leaderboard)
  const uniqueRaces = [
    ...new Set((leaderboard || []).map((item) => item.race).filter(Boolean)),
  ];

  const recentResultsColumns = [
    {
      title: "เวลา",
      dataIndex: "time",
      key: "time",
      width: 150,
      render: (time) => moment(time).format("DD/MM/YYYY HH:mm:ss"),
      sorter: (a, b) => moment(a.time).unix() - moment(b.time).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: "เลขบิบ",
      dataIndex: "bib",
      key: "bib",
      width: 100,
      sorter: (a, b) => a.bib - b.bib,
    },
    {
      title: "ชื่อ-สกุล",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Checkpoint",
      dataIndex: "position",
      key: "position",
      width: 150,
    },
    {
      title: "Race",
      dataIndex: "race",
      key: "race",
      width: 150,
      render: (race) => (race ? <Tag color="blue">{race}</Tag> : "-"),
    },
  ];

  // Helper function to check checkpoint sequence and find gaps
  const checkCheckpointSequence = (record) => {
    if (!record.missingCheckpoints || record.missingCheckpoints.length === 0) {
      return { isValid: true, gaps: [], sortedMissing: [] };
    }
    
    const missing = [...record.missingCheckpoints].sort((a, b) => a - b);
    const currentPos = record.currentPosition || 0;
    
    // Find gaps in sequence (missing checkpoints before current position)
    const gaps = [];
    if (currentPos > 0) {
      // Check if there are missing checkpoints before the current position
      for (let i = 1; i < currentPos; i++) {
        if (missing.includes(i)) {
          gaps.push(i);
        }
      }
    }
    
    return {
      isValid: gaps.length === 0,
      gaps: gaps,
      sortedMissing: missing,
    };
  };

  // Calculate rank based on status and finalTime
  const calculateRank = (leaderboard) => {
    return leaderboard.map((item, index) => {
      const sequenceCheck = checkCheckpointSequence(item);
      return {
        ...item,
        rank: index + 1,
        sequenceCheck: sequenceCheck,
      };
    });
  };

  const rankedLeaderboard = calculateRank(filteredLeaderboard);

  const leaderboardColumns = [
    {
      title: "ลำดับ",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      fixed: "left",
      render: (rank, record) => {
        // Show rank for finished runners, checkpoint position for others
        if (record.status === "finished") {
          return <strong>{rank}</strong>;
        }
        return rank;
      },
    },
    {
      title: "เลขบิบ",
      dataIndex: "bib",
      key: "bib",
      width: 100,
      sorter: (a, b) => a.bib - b.bib,
    },
    {
      title: "ชื่อ-สกุล",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Race",
      dataIndex: "race",
      key: "race",
      width: 150,
      render: (race) => (race ? <Tag color="blue">{race}</Tag> : "-"),
    },
    {
      title: "ทีม",
      dataIndex: "team",
      key: "team",
      width: 120,
      render: (team) => team || "-",
    },
    {
      title: "เพศ",
      dataIndex: "gender",
      key: "gender",
      width: 80,
      render: (gender) => gender || "-",
    },
    {
      title: "Checkpoint ปัจจุบัน",
      dataIndex: "currentPosition",
      key: "currentPosition",
      width: 120,
      render: (position, record) => 
        position ? `CP ${position}` : "-",
    },
    {
      title: "ความคืบหน้า",
      dataIndex: "progress",
      key: "progress",
      width: 180,
      render: (progress, record) => {
        // progress is a string like "50%" or "100%"
        const percent = progress ? parseInt(progress.replace("%", "")) : 0;
        return (
          <Progress
            percent={percent}
            format={() => `${record.totalCheckpoints || 0}/${record.requiredCheckpoints || 0} (${progress || "0%"})`}
          />
        );
      },
    },
    {
      title: "Checkpoint ที่ขาด",
      dataIndex: "missingCheckpoints",
      key: "missingCheckpoints",
      width: 300,
      render: (missingCheckpoints, record) => {
        if (!missingCheckpoints || missingCheckpoints.length === 0) {
          return <Tag color="success">ครบทุก Checkpoint</Tag>;
        }
        
        const sortedMissing = [...missingCheckpoints].sort((a, b) => a - b);
        const currentPos = record.currentPosition || 0;
        
        // Separate missing checkpoints: before current position (gaps) and after
        const gapsBeforeCurrent = sortedMissing.filter(pos => pos < currentPos);
        const missingAfterCurrent = sortedMissing.filter(pos => pos >= currentPos);
        
        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {gapsBeforeCurrent.length > 0 && (
              <div>
                <Typography.Text type="danger" strong style={{ fontSize: 12 }}>
                   ขาดก่อนตำแหน่งปัจจุบัน (CP {currentPos}):
                </Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Space size={[4, 4]} wrap>
                    {gapsBeforeCurrent.map((cp) => (
                      <Tag key={cp} color="error">
                        CP {cp}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>
            )}
            {missingAfterCurrent.length > 0 && (
              <div>
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  ขาดหลังจากตำแหน่งปัจจุบัน:
                </Typography.Text>
                <div style={{ marginTop: 4 }}>
                  <Space size={[4, 4]} wrap>
                    {missingAfterCurrent.map((cp) => (
                      <Tag key={cp} color="orange">
                        CP {cp}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>
            )}
            {gapsBeforeCurrent.length === 0 && missingAfterCurrent.length > 0 && (
              <div>
                <Space size={[4, 4]} wrap>
                  {sortedMissing.map((cp) => (
                    <Tag key={cp} color="orange">
                      CP {cp}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
            <Button 
              size="small" 
              type="primary" 
              loading={addingCheckpoints}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("Button clicked, record:", record);
                handleOpenPositionModal(record);
              }}
              style={{ marginTop: 4 }}
            >
              เพิ่ม Checkpoint ที่ขาด
            </Button>
          </Space>
        );
      },
    },
    {
      title: "Checkpoint ล่าสุด",
      dataIndex: "lastCheckpointTime",
      key: "lastCheckpointTime",
      width: 150,
      render: (time) => (time ? moment(time).format("DD/MM/YYYY HH:mm:ss") : "-"),
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const statusConfig = {
          finished: { color: "success", text: "เสร็จสิ้น" },
          in_progress: { color: "processing", text: "กำลังแข่ง" },
          not_started: { color: "default", text: "ยังไม่เริ่ม" },
          dnf: { color: "error", text: "ไม่จบ" },
        };
        const config = statusConfig[status] || { color: "default", text: status };
        return <Badge status={config.color} text={config.text} />;
      },
    },
    {
      title: "เวลาสุดท้าย",
      dataIndex: "finalTime",
      key: "finalTime",
      width: 150,
      render: (time) => (time ? time : "-"),
      sorter: (a, b) => {
        // Status priority: finished > in_progress > not_started
        const statusPriority = { finished: 1, in_progress: 2, not_started: 3 };
        const aPriority = statusPriority[a.status] || 99;
        const bPriority = statusPriority[b.status] || 99;
        
        // If different status, sort by status priority
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // If both finished, sort by finalTime (ascending - fastest first)
        if (a.status === "finished" && b.status === "finished") {
          if (!a.finalTime) return 1;
          if (!b.finalTime) return -1;
          // Compare time strings in HH:mm:ss format
          return a.finalTime.localeCompare(b.finalTime);
        }
        
        // If both in_progress, sort by lastCheckpointTime (most recent first)
        if (a.status === "in_progress" && b.status === "in_progress") {
          if (!a.lastCheckpointTime) return 1;
          if (!b.lastCheckpointTime) return -1;
          return moment(b.lastCheckpointTime).unix() - moment(a.lastCheckpointTime).unix();
        }
        
        // For not_started or same status without time, maintain original order
        return 0;
      },
      defaultSortOrder: "ascend",
    },
  ];

  const handleOpenPositionModal = (record) => {
    console.log("Opening modal for record:", record);
    if (!record || !record.bib) {
      console.error("Invalid record:", record);
      return;
    }
    const missingCheckpoints = record.missingCheckpoints || [];
    console.log("Setting state - bib:", record.bib, "positions:", missingCheckpoints);
    
    // Use a single state update to ensure all states are set together
    setSelectedBib(record.bib);
    setAvailablePositions([...missingCheckpoints]);
    setSelectedPositions([...missingCheckpoints]);
    // Set default datetime to current date and time
    setCheckpointDateTime(moment());
    
    // Force modal to show
    setTimeout(() => {
      setPositionModalVisible(true);
      console.log("Modal visible set to true");
    }, 10);
  };

  const handleAddMissingCheckpoints = async (bib, positions = null, dateTime = null) => {
    if (!slug) {
      message.error("ไม่พบ Event slug");
      return false;
    }

    try {
      const variables = {
        slug,
        bib,
      };
      
      // Only include positions if they are explicitly selected
      if (positions && positions.length > 0) {
        variables.positions = positions;
      }

      // Include datetime if provided
      if (dateTime && moment.isMoment(dateTime) && dateTime.isValid()) {
        // Format as ISO string or custom format that backend expects
        // Backend likely expects format like "YYYY-MM-DD HH:mm:ss" or ISO string
        variables.defaultTime = dateTime.format("YYYY-MM-DD HH:mm:ss");
      } else if (dateTime) {
        message.warning("รูปแบบวันที่และเวลาไม่ถูกต้อง");
        return false;
      }

      const { data } = await addMissingCheckpoints({
        variables,
      });

      if (data?.addMissingCheckpoints?.[0]?.success) {
        const result = data.addMissingCheckpoints[0];
        message.success(
          `เพิ่ม Checkpoint สำเร็จ: ${result.addedCount} Checkpoint สำหรับ Bib ${result.bib}`
        );
        // Refetch data to update the UI
        await refetch();
        setPositionModalVisible(false);
        setSelectedPositions([]);
        setSelectedBib(null);
        setCheckpointDateTime(null);
        return true;
      } else {
        const errorMsg = data?.addMissingCheckpoints?.[0]?.message || "ไม่สามารถเพิ่ม Checkpoint ได้";
        message.error(errorMsg);
        return false;
      }
    } catch (error) {
      console.error("Error adding missing checkpoints:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
      return false;
    }
  };

  const handleBulkAddWithPositions = async (bibs, positions = null) => {
    if (!slug) {
      message.error("ไม่พบ Event slug");
      return;
    }

    try {
      const variables = {
        slug,
        bibs,
      };
      
      // Only include positions if they are explicitly selected
      if (positions && positions.length > 0) {
        variables.positions = positions;
      }

      const { data } = await addMissingCheckpoints({
        variables,
      });

      let successCount = 0;
      let totalAdded = 0;

      if (data?.addMissingCheckpoints) {
        data.addMissingCheckpoints.forEach((result) => {
          if (result.success) {
            successCount++;
            totalAdded += result.addedCount;
          }
        });
      }

      message.success(
        `เพิ่ม Checkpoint สำเร็จ: ${successCount}/${bibs.length} คน, รวม ${totalAdded} Checkpoint`
      );
      // Refetch data to update the UI
      await refetch();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Error adding missing checkpoints:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
    }
  };

  const handleBulkAddMissingCheckpoints = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("กรุณาเลือกนักวิ่งที่ต้องการเพิ่ม Checkpoint");
      return;
    }

    if (!slug) {
      message.error("ไม่พบ Event slug");
      return;
    }

    const bibs = selectedRowKeys.map((key) => parseInt(key.replace("leaderboard-", "")));
    
    // Get all missing positions from selected runners
    const allMissingPositions = new Set();
    filteredLeaderboard
      .filter((item) => bibs.includes(item.bib))
      .forEach((item) => {
        if (item.missingCheckpoints && item.missingCheckpoints.length > 0) {
          item.missingCheckpoints.forEach((pos) => allMissingPositions.add(pos));
        }
      });

    const positionsArray = Array.from(allMissingPositions).sort((a, b) => a - b);

    Modal.confirm({
      title: "เพิ่ม Checkpoint ที่ขาดสำหรับนักวิ่งที่เลือก?",
      content: (
        <div>
          <p>จะเพิ่ม Checkpoint สำหรับ {bibs.length} คน (Bib: {bibs.join(", ")})</p>
          <p style={{ marginTop: 8 }}>
            Checkpoint ที่จะเพิ่ม: {positionsArray.length > 0 
              ? positionsArray.map(p => `CP${p}`).join(", ")
              : "ไม่มี Checkpoint ที่ขาด"}
          </p>
        </div>
      ),
      okText: "เพิ่มทั้งหมด",
      cancelText: "ยกเลิก",
      width: 500,
      onOk: async () => {
        await handleBulkAddWithPositions(bibs, null); // Add all missing positions
      },
    });
  };

  const handleExport = () => {
    const excelData = rankedLeaderboard.map((item, index) => ({
      Rank: index + 1,
      Bib: item.bib,
      Name: item.name || "-",
      Race: item.race || "-",
      Team: item.team || "-",
      Gender: item.gender || "-",
      CurrentCheckpoint: item.currentPosition || "-",
      TotalCheckpoints: item.totalCheckpoints || 0,
      RequiredCheckpoints: item.requiredCheckpoints || 0,
      Progress: item.progress || "0%",
      MissingCheckpoints: item.missingCheckpoints && item.missingCheckpoints.length > 0
        ? item.missingCheckpoints.map(cp => `CP${cp}`).join(", ")
        : "ครบทุก Checkpoint",
      LastCheckpointTime: item.lastCheckpointTime 
        ? moment(item.lastCheckpointTime).format("DD/MM/YYYY HH:mm:ss") 
        : "-",
      Status: item.status || "-",
      FinalTime: item.finalTime || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leaderboard");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileName = `results_monitoring_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, fileName);
  };

  return (
    <Layout>
      <Layout.Content style={{ padding: "24px" }}>
        <Space style={{ width: "100%" }} direction="vertical" size="large">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={`/event/${organizId}`}>การแข่งขันทั้งหมด</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{event.name}</Breadcrumb.Item>
            <Breadcrumb.Item>ผลการตรวจสอบ</Breadcrumb.Item>
          </Breadcrumb>

          <Title level={2}>ผลการตรวจสอบ (Results Monitoring)</Title>

          {/* Summary Statistics Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="นักวิ่งทั้งหมด"
                  value={summary?.totalRunners || 0}
                  prefix={<TrophyOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="เริ่มแข่งแล้ว"
                  value={summary?.runnersStarted || 0}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="จบการแข่งขัน"
                  value={summary?.runnersFinished || 0}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="กำลังแข่ง"
                  value={summary?.runnersInProgress || 0}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="จบล่าสุด (10 นาที)"
                  value={summary?.recentFinishers || 0}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="อัพเดทล่าสุด"
                  value={lastUpdate ? moment(lastUpdate).format("HH:mm:ss") : "-"}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Button
                  type={autoRefresh ? "primary" : "default"}
                  icon={<ReloadOutlined />}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  block
                >
                  {autoRefresh ? "Auto Refresh: ON" : "Auto Refresh: OFF"}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  block
                >
                  รีเฟรชข้อมูล
                </Button>
              </Card>
            </Col>
          </Row>

          {/* Filters and Actions */}
          <Card>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={12}>
                  <Input
                    placeholder="ค้นหาเลขบิบหรือชื่อ"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={12}>
                  <Select
                    placeholder="เลือก Race"
                    style={{ width: "100%" }}
                    value={selectedRace}
                    onChange={setSelectedRace}
                    allowClear
                  >
                    {uniqueRaces.map((race) => (
                      <Option key={race} value={race}>
                        {race}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <Space>
                <Button type="primary" onClick={handleExport}>
                  Export to Excel
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={handleBulkAddMissingCheckpoints}
                  disabled={selectedRowKeys.length === 0}
                  loading={addingCheckpoints}
                >
                  เพิ่ม Checkpoint ที่ขาด ({selectedRowKeys.length})
                </Button>
                <Button
                  onClick={() => {
                    setSearchText("");
                    setSelectedRace(null);
                  }}
                >
                  ล้างตัวกรอง
                </Button>
              </Space>
            </Space>
          </Card>

          {/* Leaderboard Table */}
          <Card title={`ตารางคะแนน (แสดง ${filteredLeaderboard.length} รายการ)`}>
            <Table
              scroll={{ x: true }}
              rowKey={(record) => `leaderboard-${record.bib}`}
              columns={leaderboardColumns}
              dataSource={rankedLeaderboard}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                getCheckboxProps: (record) => ({
                  disabled: !record.missingCheckpoints || record.missingCheckpoints.length === 0,
                }),
              }}
              pagination={{
                pageSize: 1000,
                showSizeChanger: true,
                showTotal: (total) => `ทั้งหมด ${total} รายการ`,
              }}
              size="small"
            />
          </Card>

          {/* Recent Results Table */}
          <Card title={`ผลการตรวจสอบล่าสุด (แสดง ${filteredRecentResults.length} รายการ)`}>
            <Table
              scroll={{ x: true }}
              rowKey={(record) => `recent-${record.bib}-${record.time}`}
              columns={recentResultsColumns}
              dataSource={filteredRecentResults}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `ทั้งหมด ${total} รายการ`,
              }}
              size="small"
            />
          </Card>
        </Space>

        {/* Position Selection Modal */}
        <Modal
          title={selectedBib ? `เลือก Checkpoint ที่ต้องการเพิ่ม - Bib ${selectedBib}` : "เลือก Checkpoint ที่ต้องการเพิ่ม"}
          visible={positionModalVisible}
          maskClosable={false}
          onOk={async () => {
            if (selectedPositions.length === 0) {
              message.warning("กรุณาเลือก Checkpoint อย่างน้อย 1 ตัว");
              return false; // Prevent modal from closing
            }
            const success = await handleAddMissingCheckpoints(selectedBib, selectedPositions, checkpointDateTime);
            // Modal will close automatically on success (when function returns true)
            // Return false to prevent closing on error
            return success !== false;
          }}
          onCancel={() => {
            setPositionModalVisible(false);
            setSelectedPositions([]);
            setSelectedBib(null);
            setCheckpointDateTime(null);
          }}
          okText="เพิ่ม Checkpoint ที่เลือก"
          cancelText="ยกเลิก"
          width={500}
          confirmLoading={addingCheckpoints}
        >
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <Typography.Text strong>เลือก Checkpoint ที่ต้องการเพิ่ม:</Typography.Text>
            </div>
            {availablePositions && availablePositions.length > 0 ? (
              <Checkbox.Group
                value={selectedPositions}
                onChange={setSelectedPositions}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" size="small">
                  {availablePositions.map((position) => (
                    <Checkbox key={position} value={position}>
                      <Tag color="error">CP {position}</Tag>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            ) : (
              <Typography.Text type="secondary">ไม่มี Checkpoint ที่ขาด</Typography.Text>
            )}
            <div>
              <Button
                size="small"
                onClick={() => setSelectedPositions([...availablePositions])}
              >
                เลือกทั้งหมด
              </Button>
              <Button
                size="small"
                style={{ marginLeft: 8 }}
                onClick={() => setSelectedPositions([])}
              >
                ยกเลิกการเลือกทั้งหมด
              </Button>
            </div>
            {selectedPositions.length > 0 && (
              <div>
                <Typography.Text type="secondary">
                  เลือกแล้ว: {selectedPositions.length} Checkpoint ({[...selectedPositions].sort((a, b) => a - b).map(p => `CP${p}`).join(", ")})
                </Typography.Text>
              </div>
            )}
            <div>
              <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
                วันที่และเวลาที่ต้องการเพิ่ม:
              </Typography.Text>
              <DatePicker
                showTime
                format="DD/MM/YYYY HH:mm:ss"
                placeholder="เลือกวันที่และเวลา"
                value={checkpointDateTime}
                onChange={(date) => setCheckpointDateTime(date)}
                style={{ width: "100%" }}
                allowClear={false}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                หากไม่ระบุวันที่และเวลา ระบบจะใช้วันที่และเวลาปัจจุบัน
              </Typography.Text>
            </div>
          </Space>
        </Modal>
      </Layout.Content>
    </Layout>
  );
}

export default ResultsMonitoring;

